// Състоянието на маса в момент T от времевата линия — чиста логика без React
// (тестове: tableStatus.test.ts). Правилата са в спецификацията на модула:
//
// ЗАЕТА — (1) настанена/приключила резервация, чийто интервал
//   [seatedAt, releasedAt) покрива T (настанената — докато не я освободят),
//   или (2) отворена сметка с името на масата (без значение на главни/малки)
//   от отварянето ѝ до края на днешния ден (приемаме, че остава заета).
// РЕЗЕРВИРАНА — предстояща резервация с [start, start + продължителност) ∋ T;
//   „сега" закъсняла (часът ѝ тече) — с червена точка. След края си тя е
//   „Неизпълнена" (expired) и не заема масата.
// СВОБОДНА — иначе; чипът над нея е часът на следващата ПРЕДСТОЯЩА
//   резервация след T, а „Свободна до" — първото нещо след T, което ще я
//   заеме (предстояща, настаняване или отворена сметка).

import type { OpenTable, Reservation } from "../../../api/reservations";
import { MINUTE_MS, endOfLocalDay, toDateKey } from "./dayTime";

/** Масата, както я вижда логиката (елемент от схемата с kind=table). */
export interface FloorTable {
  id: number;
  name: string;
}

export type TableState =
  | {
      kind: "occupied";
      // От кога е заета (настаняването или отварянето на сметката).
      sinceMs: number;
      // Резервацията, която я заема (null — сметка без резервация).
      reservation: Reservation | null;
      // Предстояща резервация за същия час (или закъсняла „сега"), докато
      // масата е заета от друго — поповърът я показва с действията ѝ, иначе
      // няма откъде да се откаже.
      reservedMeanwhile: Reservation | null;
      next: Reservation | null;
      hasLateArrival: boolean;
    }
  | {
      kind: "reserved";
      reservation: Reservation;
      next: Reservation | null;
      hasLateArrival: boolean;
    }
  | {
      kind: "free";
      next: Reservation | null;
      // Кога масата ще бъде заета/резервирана след момента (null — до края
      // на деня): предстояща, настаняване или отваряне на сметка.
      freeUntilMs: number | null;
      hasLateArrival: boolean;
    };

export interface FloorMoment {
  reservations: Reservation[];
  openTables: OpenTable[];
  // Моментът от времевата линия.
  momentMs: number;
  // Истинското „сега".
  nowMs: number;
  // Потребителят е на „сега" (закъсненията се виждат само тогава).
  isAtNow: boolean;
}

const time = (iso: string) => Date.parse(iso);

// Настаняване/отваряне на сметка — до минутата. Часовникът на страницата
// („сега") се обновява в началото на всяка минута, затова маса, настанена в
// 18:31:41, иначе би изглеждала свободна към „сега" = 18:31:00 до следващата
// минута — точно след натискането на „Настани".
const minuteOf = (iso: string) => Math.floor(time(iso) / MINUTE_MS) * MINUTE_MS;

export const reservationStartMs = (reservation: Reservation) => time(reservation.startAt);

export const reservationEndMs = (reservation: Reservation) =>
  reservationStartMs(reservation) + reservation.durationMinutes * MINUTE_MS;

/**
 * Името на маса/сметка за сравнение — огледало на BE `table_occupancy_key`
 * (reservations/table_keys.py): без интервали в краищата, без `*` от офлайн
 * replay („2" → „2*" при заето име — същата физическа маса), малки букви.
 * `toLocaleLowerCase` съвпада с Python `casefold` за кирилица, латиница,
 * цифри, интервали и „*" (всичко, което реално е в имената на масите); за
 * екзотичен Unicode (ß → „ss", гръцка крайна сигма, лигатури, BOM) двете
 * дават различен ключ — съзнателно не се гони.
 */
export const tableNameKey = (name: string) =>
  name.trim().replace(/\*+$/, "").trim().toLocaleLowerCase();

/**
 * ВСИЧКИ сметки на резервацията — главната (`billId`, първата маса) първа,
 * после сметките на другите маси при „Отделна сметка за всяка маса" и
 * отделените с „Раздели". По-стар BE и по-стара каса в локален режим не
 * пращат `billIds` → само `billId`.
 */
export const reservationBillIds = (reservation: Reservation): number[] => {
  if (reservation.billIds && reservation.billIds.length > 0) return reservation.billIds;
  return reservation.billId === null ? [] : [reservation.billId];
};

/** Резервацията е за днешния локален ден (спрямо „сега"). */
export const isReservationToday = (reservation: Reservation, nowMs: number) =>
  toDateKey(new Date(reservationStartMs(reservation))) === toDateKey(new Date(nowMs));

/**
 * Предстояща, чийто час [start, край) вече тече — закъснява, но още държи
 * масата (и през полунощ: 23:30–01:30 тече и в 00:30 на новия ден).
 */
export const isRunningUpcoming = (reservation: Reservation, nowMs: number) =>
  reservation.status === "upcoming" && reservationStartMs(reservation) <= nowMs && nowMs < reservationEndMs(reservation);

/**
 * Предстояща, чийто край (начало + продължителност) е минал, без да е
 * настанена или отказана — „Неизпълнена". Огледало на BE синхронизацията
 * при четене (status "expired", docs/cross-repo-contract.md); екранът не
 * чака следващия GET.
 */
export const isExpiredUpcoming = (reservation: Reservation, nowMs: number) =>
  reservation.status === "upcoming" && reservationEndMs(reservation) <= nowMs;

/**
 * Цели минути закъснение на предстояща, докато часът ѝ тече (0 — не
 * закъснява). В самата минута на часа (19:00:00–19:00:59) още не закъснява —
 * иначе „Закъснява 0 мин" с червена „!" веднага щом часовникът тикне; след
 * края си не „закъснява 25 ч" — тя е „Неизпълнена".
 */
const lateMinutes = (reservation: Reservation, nowMs: number) => {
  if (!isRunningUpcoming(reservation, nowMs)) return 0;
  return Math.max(0, Math.floor((nowMs - reservationStartMs(reservation)) / MINUTE_MS));
};

/** Предстояща, закъсняла с поне една пълна минута. */
export const isLate = (reservation: Reservation, nowMs: number) => lateMinutes(reservation, nowMs) >= 1;

/** Резервацията е на тази маса. */
const isOnTable = (reservation: Reservation, table: FloorTable) =>
  reservation.tableIds.includes(table.id);

const byStart = (left: Reservation, right: Reservation) =>
  reservationStartMs(left) - reservationStartMs(right);

/**
 * Интервалът, в който настанена/приключила резервация заема масите си.
 * Приключилата — поне една минута: настаняване и освобождаване в една и съща
 * минута иначе дава празен интервал и резервацията не се вижда никъде.
 */
export const occupancyInterval = (reservation: Reservation, nowMs: number) => {
  if (!reservation.seatedAt) return null;
  const startMs = minuteOf(reservation.seatedAt);
  if (reservation.status === "seated") {
    return { startMs, endMs: Number.POSITIVE_INFINITY };
  }
  if (reservation.status === "completed") {
    const rawEndMs = reservation.releasedAt ? minuteOf(reservation.releasedAt) : nowMs;
    return { startMs, endMs: Math.max(rawEndMs, startMs + MINUTE_MS) };
  }
  return null;
};

/**
 * Настанената/приключилата заема масата в момента. Приключилата — само в
 * МИНАЛ момент: към „сега" (или след него) тя вече е освободена, дори когато
 * часовникът на страницата изостава от `releasedAt` (фонов таб) или
 * интервалът е разширен до минута.
 */
export const occupiesAt = (reservation: Reservation, momentMs: number, nowMs: number) => {
  const interval = occupancyInterval(reservation, nowMs);
  if (!interval || momentMs < interval.startMs || momentMs >= interval.endMs) return null;
  if (reservation.status === "completed" && momentMs >= nowMs) return null;
  return interval;
};

/** Следващата предстояща резервация на масата СЛЕД момента. */
export const nextUpcomingAfter = (
  table: FloorTable,
  reservations: Reservation[],
  momentMs: number,
): Reservation | null =>
  reservations
    .filter(
      (reservation) =>
        reservation.status === "upcoming" &&
        isOnTable(reservation, table) &&
        reservationStartMs(reservation) > momentMs,
    )
    .sort(byStart)[0] ?? null;

/**
 * До кога е свободна масата след момента: най-ранното от следващата
 * предстояща, настаняване на резервация и отваряне на сметка (днес) след
 * момента. null — до края на деня.
 */
const freeUntilAfter = (
  table: FloorTable,
  onTable: Reservation[],
  openTables: OpenTable[],
  momentMs: number,
  nowMs: number,
): number | null => {
  const candidates: number[] = [];
  for (const reservation of onTable) {
    if (reservation.status === "upcoming") candidates.push(reservationStartMs(reservation));
    const interval = occupancyInterval(reservation, nowMs);
    if (interval) candidates.push(interval.startMs);
  }
  const nameKey = tableNameKey(table.name);
  if (momentMs < endOfLocalDay(nowMs)) {
    for (const bill of openTables) {
      if (tableNameKey(bill.tableName) === nameKey) candidates.push(minuteOf(bill.openedAt));
    }
  }
  const after = candidates.filter((candidate) => candidate > momentMs);
  return after.length > 0 ? Math.min(...after) : null;
};

/** Състоянието на масата в момента `floor.momentMs`. */
export const tableStateAt = (table: FloorTable, floor: FloorMoment): TableState => {
  const { reservations, openTables, momentMs, nowMs, isAtNow } = floor;
  const onTable = reservations.filter((reservation) => isOnTable(reservation, table));
  const next = nextUpcomingAfter(table, reservations, momentMs);
  const hasLateArrival = isAtNow && onTable.some((reservation) => isLate(reservation, nowMs));

  const reservedNow = onTable
    .filter(
      (reservation) =>
        reservation.status === "upcoming" &&
        reservationStartMs(reservation) <= momentMs &&
        momentMs < reservationEndMs(reservation),
    )
    .sort(byStart)[0];
  const seatedHere = onTable
    .map((reservation) => ({ reservation, interval: occupiesAt(reservation, momentMs, nowMs) }))
    .filter(({ interval }) => interval !== null)
    .sort((left, right) => left.interval!.startMs - right.interval!.startMs)[0];
  if (seatedHere) {
    return {
      kind: "occupied",
      sinceMs: seatedHere.interval!.startMs,
      reservation: seatedHere.reservation,
      reservedMeanwhile: reservedNow ?? null,
      next,
      hasLateArrival,
    };
  }

  // Отворената сметка се прожектира до края на днешния ден ВКЛЮЧИТЕЛНО
  // (плъзгачът на 24:00 е точно краят на деня — масата още е заета).
  const nameKey = tableNameKey(table.name);
  const openBill = openTables
    .filter(
      (bill) =>
        tableNameKey(bill.tableName) === nameKey &&
        minuteOf(bill.openedAt) <= momentMs &&
        momentMs <= endOfLocalDay(nowMs),
    )
    .sort((left, right) => time(left.openedAt) - time(right.openedAt))[0];
  if (openBill) {
    return {
      kind: "occupied",
      sinceMs: minuteOf(openBill.openedAt),
      reservation: null,
      reservedMeanwhile: reservedNow ?? null,
      next,
      hasLateArrival,
    };
  }

  if (reservedNow) {
    return { kind: "reserved", reservation: reservedNow, next, hasLateArrival };
  }

  return {
    kind: "free",
    next,
    freeUntilMs: freeUntilAfter(table, onTable, openTables, momentMs, nowMs),
    hasLateArrival,
  };
};

/** Кешира състоянията на масите за един момент (платно + табове + поповър). */
export const createFloorSnapshot = (floor: FloorMoment) => {
  const cache = new Map<number, TableState>();
  return {
    ...floor,
    stateOf: (table: FloorTable): TableState => {
      const cached = cache.get(table.id);
      if (cached) return cached;
      const state = tableStateAt(table, floor);
      cache.set(table.id, state);
      return state;
    },
  };
};

export type FloorSnapshot = ReturnType<typeof createFloorSnapshot>;

// „Скоро": колко преди часа на резервацията списъкът/поповърът казва
// „Пристига след" — и прозорецът, в който резервация НЕ може да вземе маса с
// отворена сметка (без резервация / не нейната): гостите още седят и не знаем
// кога ще си тръгнат (`isTableBusyDuring`). Огледало на BE
// `ARRIVING_SOON_MINUTES` (app/features/reservations/models.py; create/update →
// 409 „Маса 4 в момента е заета…", същият прозорец и за предупреждението в
// Маркиране); описана в docs/cross-repo-contract.md. Смениш ли я — и в BE.
export const ARRIVING_SOON_MINUTES = 60;

/**
 * Редът за пристигането спрямо истинското „сега": закъснява с N минути,
 * пристига след N минути (само в следващия час) или нищо.
 */
export const arrivalStatus = (
  reservation: Reservation,
  nowMs: number,
): { kind: "late" | "soon"; minutes: number } | null => {
  if (reservation.status !== "upcoming") return null;
  const startMs = reservationStartMs(reservation);
  if (startMs <= nowMs) {
    // В минутата на часа — нито „закъснява 0 мин", нито „след 0 мин".
    const late = lateMinutes(reservation, nowMs);
    return late >= 1 ? { kind: "late", minutes: late } : null;
  }
  const minutes = Math.ceil((startMs - nowMs) / MINUTE_MS);
  return minutes <= ARRIVING_SOON_MINUTES ? { kind: "soon", minutes } : null;
};

/**
 * Откога резервацията държи масите си: планираното начало, а настанената
 * ПО-РАНО (до 30 мин преди часа) — от настаняването. Огледало на BE
 * `reservation_hold_start` (app/features/reservations/clock.py).
 */
export const reservationHoldStartMs = (reservation: Reservation) => {
  const startMs = reservationStartMs(reservation);
  if (reservation.status !== "seated" || !reservation.seatedAt) return startMs;
  return Math.min(startMs, minuteOf(reservation.seatedAt));
};

/**
 * За режима „избор на маса" (Нова резервация): масата е заета в интервала
 * [fromMs, toMs), ако се застъпва с активна (предстояща/настанена) резервация
 * — по ПЛАНИРАНИЯ ѝ час [start, start + продължителност), както проверява BE
 * (настанената по-рано — от настаняването, `reservationHoldStartMs`);
 * долепените не се застъпват (15:00 за 1 ч → нова от 16:00 е позволена) —
 * или, за интервал, започващ в следващите `ARRIVING_SOON_MINUTES`, ако на
 * масата има отворена сметка без резервация (не знаем кога ще си тръгнат;
 * BE също отказва с 409). Настанената пази масата само до планирания си
 * край: сметката ѝ на НЕЙНА маса НЕ блокира нова резервация след него, дори
 * гостите още да седят; сметката ѝ на друга маса блокира като всяка друга. Още отворената сметка на приключила резервация е като сметка без
 * резервация. `excludeReservationId` — редактираната резервация
 * (застъпване със себе си няма, както в BE).
 */
export const isTableBusyDuring = (
  table: FloorTable,
  fromMs: number,
  toMs: number,
  floor: Pick<FloorMoment, "reservations" | "openTables" | "nowMs"> & {
    excludeReservationId?: number | null;
  },
): boolean => {
  const reservations =
    floor.excludeReservationId == null
      ? floor.reservations
      : floor.reservations.filter((reservation) => reservation.id !== floor.excludeReservationId);
  const overlapsReservation = reservations.some(
    (reservation) =>
      (reservation.status === "upcoming" || reservation.status === "seated") &&
      isOnTable(reservation, table) &&
      reservationHoldStartMs(reservation) < toMs &&
      fromMs < reservationEndMs(reservation),
  );
  if (overlapsReservation) return true;
  const startsSoon = fromMs < floor.nowMs + ARRIVING_SOON_MINUTES * MINUTE_MS;
  if (!startsSoon) return false;
  // Сметката на настанена резервация не заема масата, само ако масата е НЕЙНА;
  // отделена с „Раздели" (или преместена) на друга маса заема масата като
  // всяка отворена сметка (BE `seated_bill_table_keys`).
  const heldByReservationHere = new Set(
    reservations
      .filter((reservation) => reservation.status === "seated" && isOnTable(reservation, table))
      .flatMap((reservation) => reservationBillIds(reservation)),
  );
  const nameKey = tableNameKey(table.name);
  return floor.openTables.some(
    (bill) => !heldByReservationHere.has(bill.billId) && tableNameKey(bill.tableName) === nameKey,
  );
};
